import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FriendDevice, Identity } from '../types';
import { errorMessage } from '../lib/db';
import { isValidUsername, signOut } from '../lib/identity';
import { deleteDevice, fetchOwnDevices, renameUsername } from '../lib/relay';
import {
  installInstructions,
  isInstalled,
  promptInstall,
  subscribeInstall,
} from '../lib/pwa';
import { clearAll, saveIdentity } from '../lib/store';

type Props = {
  me: Identity;
  onBack: () => void;
  onSignedOut: () => void;
  onRenamed: (updated: Identity) => void;
};

const APP_VERSION = '0.1.0-week1';

export function Settings({ me, onBack, onSignedOut, onRenamed }: Props) {
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<null | 'clear-transcripts' | 'sign-out' | 'export'>(null);
  const [exportedJson, setExportedJson] = useState<string | null>(null);
  const [stats, setStats] = useState<{ messages: number; transcripts: number; storageKB: number }>(
    () => readStats(),
  );

  // Linked devices state.
  const [devices, setDevices] = useState<FriendDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [devicesError, setDevicesError] = useState<string | null>(null);

  const refreshDevices = useCallback(async () => {
    setDevicesLoading(true);
    setDevicesError(null);
    try {
      const fetched = await fetchOwnDevices(me.id);
      setDevices(fetched);
    } catch (err) {
      setDevicesError(errorMessage(err));
    } finally {
      setDevicesLoading(false);
    }
  }, [me.id]);

  useEffect(() => {
    void refreshDevices();
  }, [refreshDevices]);

  async function handleUnlinkDevice(deviceId: string) {
    setBusy(`unlink-${deviceId}`);
    try {
      await deleteDevice(deviceId);
      await refreshDevices();
    } catch (err) {
      setDevicesError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  // Install status (this device).
  const [, forceInstall] = useState(0);
  const [installSteps, setInstallSteps] = useState(false);
  useEffect(() => subscribeInstall(() => forceInstall((n) => n + 1)), []);
  const installedHere = isInstalled();

  async function handleInstall() {
    const outcome = await promptInstall();
    // No native prompt available (iOS, or Chrome hasn't offered one yet) —
    // reveal the manual steps instead of doing nothing.
    if (outcome === 'unavailable') setInstallSteps(true);
  }

  // Rename flow.
  const [newName, setNewName] = useState(me.username);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renameNote, setRenameNote] = useState<string | null>(null);

  async function handleRename(e: React.FormEvent) {
    e.preventDefault();
    setRenameError(null);
    setRenameNote(null);
    const clean = newName.trim().toLowerCase();
    if (clean === me.username) {
      setRenameError("That's your current username.");
      return;
    }
    if (!isValidUsername(clean)) {
      setRenameError('Username: 2–32 chars, lowercase letters, digits, _ . - only.');
      return;
    }
    setBusy('rename');
    try {
      await renameUsername(me.id, clean);
      const updated = { ...me, username: clean };
      saveIdentity(updated);
      onRenamed(updated);
      setNewName(clean);
      setRenameNote(`You're now @${clean}.`);
    } catch (err) {
      setRenameError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  // Re-read stats when the page mounts, in case the user opened it after
  // a long session and the numbers have changed.
  useEffect(() => {
    setStats(readStats());
  }, []);

  async function handleClearTranscripts() {
    setBusy('clear-transcripts');
    try {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k?.startsWith('norush.transcript.v1.')) keys.push(k);
      }
      for (const k of keys) localStorage.removeItem(k);
      setStats(readStats());
    } finally {
      setBusy(null);
      setConfirm(null);
    }
  }

  function handleExportData() {
    // GDPR Art. 20 portability: export everything we hold locally about
    // this user. Pure JSON, downloadable. The server holds essentially
    // nothing readable, so this is the complete picture.
    const out: Record<string, unknown> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith('norush.')) {
        try {
          out[k] = JSON.parse(localStorage.getItem(k) ?? 'null');
        } catch {
          out[k] = localStorage.getItem(k);
        }
      }
    }
    const json = JSON.stringify(
      {
        exported_at: new Date().toISOString(),
        identity: { id: me.id, username: me.username },
        local_data: out,
      },
      null,
      2,
    );
    setExportedJson(json);
    setConfirm(null);
  }

  function downloadExport() {
    if (!exportedJson) return;
    const blob = new Blob([exportedJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `norush-${me.username}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleSignOut() {
    setBusy('sign-out');
    try {
      clearAll();
      await signOut().catch(() => undefined);
      onSignedOut();
    } finally {
      setBusy(null);
    }
  }

  const deviceFingerprint = useMemo(
    () =>
      me.outer_pubkey_b64
        .replace(/[+/=]/g, '')
        .slice(0, 12)
        .match(/.{4}/g)!
        .join(' '),
    [me.outer_pubkey_b64],
  );

  return (
    <div className="screen">
      <header className="topbar">
        <button className="btn btn-ghost btn-small" onClick={onBack} aria-label="Back">
          ← Back
        </button>
        <div className="topbar-center">
          <span className="me-name">Settings</span>
        </div>
        <span className="topbar-spacer" />
      </header>

      <main className="content">
        <section className="card">
          <p className="eyebrow">You</p>
          <h2 className="h-section">@{me.username}</h2>
          <dl className="kv">
            <div className="kv-row">
              <dt>Account id</dt>
              <dd className="kv-mono">{me.id}</dd>
            </div>
            <div className="kv-row">
              <dt>Identity fingerprint</dt>
              <dd className="kv-mono">{deviceFingerprint}</dd>
            </div>
          </dl>
        </section>

        <section className="card">
          <p className="eyebrow">Identity</p>
          <h2 className="h-section">Change username</h2>
          <form
            onSubmit={handleRename}
            className="form-inline"
            style={{ marginTop: 8 }}
          >
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={me.username}
              maxLength={32}
              autoComplete="off"
              disabled={busy !== null}
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={busy !== null || !newName.trim() || newName.trim() === me.username}
            >
              {busy === 'rename' ? 'Renaming…' : 'Rename'}
            </button>
          </form>
          {renameError && <p className="error">{renameError}</p>}
          {renameNote && <p className="muted" style={{ marginTop: 8 }}>{renameNote}</p>}
        </section>

        <section className="card">
          <p className="eyebrow">Your devices</p>
          <h2 className="h-section">Where you're signed in</h2>
          {devicesLoading && <p className="muted">Loading…</p>}
          {devicesError && <p className="error">{devicesError}</p>}
          {!devicesLoading && devices.length > 0 && (
            <ul className="device-list">
              {devices
                .slice()
                .sort((a, b) =>
                  a.id === me.device_id ? -1 : b.id === me.device_id ? 1 : 0,
                )
                .map((d) => {
                  const isThis = d.id === me.device_id;
                  return (
                    <li key={d.id} className="device-row">
                      <span className="device-info">
                        <span className="device-name">
                          {d.label}
                          {isThis && (
                            <span className="device-this"> · this device</span>
                          )}
                        </span>
                        <span className="device-id-mono">{d.id.slice(0, 8)}…</span>
                      </span>
                      {!isThis && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-small"
                          onClick={() => handleUnlinkDevice(d.id)}
                          disabled={busy !== null}
                        >
                          {busy === `unlink-${d.id}` ? 'Unlinking…' : 'Unlink'}
                        </button>
                      )}
                    </li>
                  );
                })}
            </ul>
          )}
          <p className="footnote">
            To add another device, open No Rush on it and verify the same
            phone number. The new install will appear here automatically.
          </p>
        </section>

        <section className="card">
          <p className="eyebrow">App</p>
          <h2 className="h-section">Install</h2>
          {installedHere ? (
            <p className="muted">
              Installed on this device. Opens from your home screen, no
              browser bar.
            </p>
          ) : (
            <>
              <p className="muted">
                Running in the browser. Add it to your home screen for a
                one-tap, full-screen app.
              </p>
              <button
                type="button"
                className="btn btn-primary btn-small"
                onClick={handleInstall}
                style={{ margin: '8px 0' }}
              >
                Install No Rush
              </button>
              {installSteps && (
                <p className="footnote">{installInstructions()}</p>
              )}
            </>
          )}
        </section>

        <section className="card">
          <p className="eyebrow">On this device</p>
          <h2 className="h-section">Local data</h2>
          <dl className="kv">
            <div className="kv-row">
              <dt>Messages stored</dt>
              <dd>{stats.messages}</dd>
            </div>
            <div className="kv-row">
              <dt>Voice transcripts cached</dt>
              <dd>{stats.transcripts}</dd>
            </div>
            <div className="kv-row">
              <dt>Total local storage</dt>
              <dd>~{stats.storageKB} KB</dd>
            </div>
          </dl>

          {stats.transcripts > 0 && (
            <button
              type="button"
              className="btn btn-ghost btn-small"
              onClick={() => setConfirm('clear-transcripts')}
              disabled={busy !== null}
              style={{ marginTop: 12 }}
            >
              Clear voice transcripts ({stats.transcripts})
            </button>
          )}
        </section>

        <section className="card">
          <p className="eyebrow">Your data</p>
          <h2 className="h-section">Export everything</h2>
          <p className="muted">A single JSON file. Yours to keep (GDPR Art. 20).</p>
          {!exportedJson ? (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleExportData}
              disabled={busy !== null}
              style={{ marginTop: 8 }}
            >
              Generate export
            </button>
          ) : (
            <div className="export-ready">
              <p className="muted">
                Ready. {exportedJson.length.toLocaleString()} bytes of JSON.
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-primary btn-small"
                  onClick={downloadExport}
                >
                  Download .json
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-small"
                  onClick={() => setExportedJson(null)}
                >
                  Discard
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="card card-negative">
          <p className="eyebrow">Leaving</p>
          <h2 className="h-section">Sign out and wipe this device</h2>
          <p className="muted">
            Erases everything here, including your secret key. Sign back in
            anytime with your phone.
          </p>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setConfirm('sign-out')}
            disabled={busy !== null}
            style={{ marginTop: 8 }}
          >
            Sign out and wipe
          </button>
        </section>

        <p className="footnote">
          No Rush prototype · {APP_VERSION} ·{' '}
          <a
            href="https://github.com/Ennui92/no-rush"
            target="_blank"
            rel="noopener"
            style={{ color: 'var(--accent-deep)' }}
          >
            source on GitHub
          </a>
        </p>
      </main>

      {confirm === 'clear-transcripts' && (
        <ConfirmDialog
          title="Clear all voice transcripts?"
          body={`This removes the ${stats.transcripts} cached transcripts from this device. The original voice notes are not affected; you can re-transcribe any of them later.`}
          confirmLabel="Clear transcripts"
          danger={false}
          onConfirm={handleClearTranscripts}
          onCancel={() => setConfirm(null)}
          busy={busy === 'clear-transcripts'}
        />
      )}

      {confirm === 'sign-out' && (
        <ConfirmDialog
          title="Sign out and wipe this device?"
          body="Your secret key lives only here. Wiping means you'll have to verify your phone again and friends will see a new safety number for you. If you have unread messages, fetch them first."
          confirmLabel="Sign out and wipe"
          danger={true}
          onConfirm={handleSignOut}
          onCancel={() => setConfirm(null)}
          busy={busy === 'sign-out'}
        />
      )}
    </div>
  );
}

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  danger,
  onConfirm,
  onCancel,
  busy,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  danger: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
}) {
  return (
    <div className="confirm-overlay" role="dialog" aria-modal="true" onClick={onCancel}>
      <div className="confirm-card" onClick={(e) => e.stopPropagation()}>
        <h3 className="h-section">{title}</h3>
        <p className="muted">{body}</p>
        <div className="confirm-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={danger ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function readStats(): { messages: number; transcripts: number; storageKB: number } {
  let messages = 0;
  let transcripts = 0;
  let totalChars = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k) continue;
    const v = localStorage.getItem(k) ?? '';
    totalChars += k.length + v.length;
    if (k.startsWith('norush.transcript.v1.')) {
      transcripts += 1;
    } else if (k === 'norush.messages.v1' || k === 'norush.group-messages.v1') {
      try {
        const parsed = JSON.parse(v);
        if (parsed && typeof parsed === 'object') {
          for (const arr of Object.values(parsed)) {
            if (Array.isArray(arr)) messages += arr.length;
          }
        }
      } catch {
        // ignore
      }
    }
  }
  return { messages, transcripts, storageKB: Math.ceil(totalChars / 1024) };
}
